import { useState, useEffect } from 'react';
import './Login.css';
import PasswordInput from './PasswordInput';
import { passwordError } from './password';
import { useI18n } from './i18n';
import Icon from './Icon';

function Login({ onLogin, onRegister, onForgot, onBack, mlsCatalog = [], initialMode = 'login', allowSignup = true, lockedMlsId = null, lockedMlsName = '', lockedEmail = null, lockedOrgId = null, lockedOrgName = '', lockedOrgMlsName = '' }) {
  const { t } = useI18n();
  const [mode, setMode]         = useState(initialMode);   // 'login' | 'signup' | 'forgot'
  const [username, setUsername] = useState('');
  // An MLS-scoped invite link pins the MLS; the recruit can't change it.
  const [mlsId, setMlsId]       = useState(lockedMlsId || '');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [info, setInfo]         = useState('');
  const [resetLink, setResetLink] = useState('');
  const [needEmail, setNeedEmail] = useState(false);
  const [busy, setBusy]         = useState(false);
  const [fullName, setFullName] = useState('');
  const [verifySent, setVerifySent] = useState('');   // email a verification link was just sent to

  // An invite link drops the recruit straight onto the signup form. Wipe any
  // credential fields the browser may have restored/autofilled so the new account
  // starts from a clean slate — never inheriting someone else's saved email/password.
  useEffect(() => {
    if (initialMode === 'signup') { setFullName(''); setEmail(''); setPassword(''); setConfirm(''); }
  }, [initialMode]);

  function switchMode(m) { setMode(m); setError(''); setInfo(''); setResetLink(''); setNeedEmail(false); setVerifySent(''); setUsername(''); setFullName(''); setPassword(''); setConfirm(''); setEmail(''); setMlsId(lockedMlsId || ''); }

  async function submit(e) {
    e.preventDefault();
    if (mode === 'login') {
      setBusy(true);
      const err = await onLogin(username, password);
      setBusy(false);
      if (err) setError(err);
      return;
    }
    if (mode === 'signup') {
      if (!lockedOrgId && !mlsId) { setError('Please select your MLS.'); return; }
      const signupEmail = (lockedEmail || email).trim();
      if (!signupEmail) { setError('Please enter your email — it’s your login, and we’ll send a verification link there.'); return; }
      if (!password)        { setError('Password is required.'); return; }
      if (password !== confirm) { setError('Passwords do not match.'); return; }
      setBusy(true);
      // The email IS the login (username); no separate username to pick.
      const r = await onRegister({ userName: signupEmail, name: fullName, password, mlsId, email: signupEmail, orgId: lockedOrgId || undefined });
      setBusy(false);
      // Public self-sign-up: no account yet — a verification email was sent.
      if (r && r.pending) { setVerifySent(r.email || signupEmail); return; }
      if (r) setError(typeof r === 'string' ? r : (r.error || 'Sign-up failed.'));
      return;
    }
    // forgot
    if (!username.trim()) { setError('Enter your username.'); return; }
    setBusy(true);
    const r = await onForgot(username, email);
    setBusy(false);
    if (r.needEmail) {
      setNeedEmail(true);
      setError(r.error || '');
      setInfo(r.error ? '' : "No email on file for this account — enter your email below and we'll save it and send the link.");
      setResetLink('');
      return;
    }
    if (r.error) { setError(r.error); return; }
    setNeedEmail(false); setError('');
    setInfo(`A reset link was sent to ${r.email}.`);
    if (r.warn) { setInfo(r.warn); setResetLink(r.link); }
  }

  // Public self-sign-up submitted: the account isn't created until the emailed link is
  // clicked. Show a "check your email" confirmation instead of the form.
  if (verifySent) {
    return (
      <div className="login-screen">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div className="login-top">
            {onBack ? <button type="button" className="login-back" onClick={onBack}>{t('login.back')}</button> : <span />}
          </div>
          <div className="login-brand"><span className="login-logo"><Icon name="brand" size={22} /></span> iBetterAgent</div>
          <h2 className="login-title">Check your email</h2>
          <p className="login-signup-note">
            We sent a verification link to <strong>{verifySent}</strong>. Open it within <strong>30 minutes</strong> to
            finish creating your account — you’ll be signed in automatically.
          </p>
          <p className="login-signup-note" style={{ marginTop: 8 }}>Didn’t get it? Check your spam folder.</p>
          <p className="login-switch">
            <button type="button" className="login-link" onClick={() => setVerifySent('')}>← Use a different email</button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={submit} noValidate autoComplete={mode === 'signup' ? 'off' : 'on'}>
        <div className="login-top">
          {onBack ? <button type="button" className="login-back" onClick={onBack}>{t('login.back')}</button> : <span />}
        </div>
        <div className="login-brand"><span className="login-logo"><Icon name="brand" size={22} /></span> iBetterAgent</div>
        <h2 className="login-title">
          {mode === 'login' ? t('login.title.login') : mode === 'signup' ? t('login.title.signup') : t('login.title.reset')}
        </h2>
        {error && <div className="login-error">⚠ {error}</div>}
        {info && (
          <div className="login-info">
            ✓ {info}
            {resetLink && <a className="login-reset-link" href={resetLink}>Open reset link</a>}
          </div>
        )}

        {mode === 'signup' && (
          <p className="login-signup-note">
            {lockedOrgId
              ? <>You're joining <strong>{lockedOrgName}</strong>{lockedOrgMlsName ? <> on <strong>{lockedOrgMlsName}</strong></> : ''} — just add your email and a password to finish.</>
              : t('login.signupNote')}
          </p>
        )}

        {/* Login / forgot use the email (or, for the operator, "sysadmin") as the identifier.
            Sign-up has no separate username field — the email below IS the login. */}
        {mode !== 'signup' && (
          <>
            <label className="login-label">{mode === 'forgot' ? t('login.label.yourUsername') : t('login.label.username')}</label>
            <input className="login-input" value={username} autoComplete="username"
              placeholder={mode === 'login' ? 'you@example.com' : ''}
              onChange={e => { setUsername(e.target.value); setError(''); }} />
          </>
        )}

        {mode === 'signup' && (
          <>
            {lockedOrgId ? (
              <>
                <label className="login-label">Organization</label>
                <div className="login-input login-input-locked">{lockedOrgName} <span className="login-locked-tag"><Icon name="lock" size={16} /> from your invitation</span></div>
                {lockedOrgMlsName && (
                  <>
                    <label className="login-label">{t('login.label.mls')}</label>
                    <div className="login-input login-input-locked">{lockedOrgMlsName} <span className="login-locked-tag"><Icon name="lock" size={16} /> via {lockedOrgName}</span></div>
                  </>
                )}
              </>
            ) : (
              <>
                <label className="login-label">{t('login.label.mls')}</label>
                {lockedMlsId ? (
                  <div className="login-input login-input-locked">{lockedMlsName || mlsCatalog.find(m => m.id === lockedMlsId)?.name || lockedMlsId} <span className="login-locked-tag"><Icon name="lock" size={16} /> set by your invitation</span></div>
                ) : (
                  <>
                    <select className="login-input" value={mlsId}
                      onChange={e => { setMlsId(e.target.value); setError(''); }}>
                      <option value="">{t('login.mlsPlaceholder')}</option>
                      {mlsCatalog.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                    <p className="login-pw-hint">{t('login.mlsHint')}</p>
                  </>
                )}
              </>
            )}
            {lockedEmail ? (
              <>
                <label className="login-label">Email</label>
                <div className="login-input login-input-locked">{lockedEmail} <span className="login-locked-tag"><Icon name="lock" size={16} /> from your invitation</span></div>
              </>
            ) : (
              <>
                <label className="login-label">Login Email</label>
                <input type="email" className="login-input" value={email} autoComplete="email" placeholder="you@example.com"
                  onChange={e => { setEmail(e.target.value); setError(''); }} />
                <p className="login-pw-hint">This is your login. We’ll email a verification link to finish creating your account.</p>
              </>
            )}
          </>
        )}

        {mode === 'forgot' && needEmail && (
          <>
            <label className="login-label">{t('login.label.emailReset')}</label>
            <input type="email" className="login-input" value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }} />
          </>
        )}

        {mode !== 'forgot' && mode !== 'signup' && (
          <>
            <label className="login-label">{t('login.label.password')}</label>
            <PasswordInput className="login-input" value={password}
              autoComplete="current-password"
              onChange={e => { setPassword(e.target.value); setError(''); }} />
          </>
        )}

        {mode === 'signup' && (
          <>
            {/* Password + Confirm sit side by side; the hint appears below only when
                the password is invalid or the two don't match. */}
            <div className="login-pw-row">
              <div className="login-pw-col">
                <label className="login-label">{t('login.label.password')}</label>
                <PasswordInput className="login-input" value={password}
                  autoComplete="new-password"
                  onChange={e => { setPassword(e.target.value); setError(''); }} />
              </div>
              <div className="login-pw-col">
                <label className="login-label">{t('login.label.confirm')}</label>
                <PasswordInput className="login-input" value={confirm}
                  autoComplete="new-password"
                  onChange={e => { setConfirm(e.target.value); setError(''); }} />
              </div>
            </div>
            {password && passwordError(password) && (
              <p className="login-pw-hint login-pw-hint-warn">{t('login.pwHint')}</p>
            )}
            {confirm && password !== confirm && (
              <p className="login-pw-hint login-pw-hint-warn">{t('login.pwMismatch')}</p>
            )}
            <label className="login-label">Full name <span className="login-optional">(optional)</span></label>
            <input className="login-input" value={fullName} autoComplete="name" placeholder="Your full name"
              onChange={e => { setFullName(e.target.value); setError(''); }} />
            <p className="login-pw-hint">Shown on your public profile and to contacts. You can add or change it later in your profile.</p>
          </>
        )}

        <button type="submit" className="login-btn" disabled={busy}>
          {mode === 'login' ? t('login.btn.login') : mode === 'signup' ? t('login.btn.signup') : (busy ? t('login.btn.sending') : t('login.btn.reset'))}
        </button>

        {mode === 'login' && (
          <p className="login-switch">
            <button type="button" className="login-link" onClick={() => switchMode('forgot')}>{t('login.forgot')}</button>
          </p>
        )}

        {(mode !== 'login' || allowSignup) && (
          <p className="login-switch">
            {mode === 'login'
              ? <>{t('login.noAccount')}{' '}
                  <button type="button" className="login-link" onClick={() => switchMode('signup')}>{t('login.signupLink')}</button></>
              : <>{t('login.haveAccount')}{' '}
                  <button type="button" className="login-link" onClick={() => switchMode('login')}>{t('login.loginLink')}</button></>}
          </p>
        )}

      </form>
    </div>
  );
}

export default Login;
